import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  ArrowBack,
  Group,
  Schedule,
  Shuffle,
  Person,
  Timer
} from '@mui/icons-material';

export default function TeeTimeManagement() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [teeTimes, setTeeTimes] = useState([]);
  const [unassignedPlayers, setUnassignedPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openTeeTimeDialog, setOpenTeeTimeDialog] = useState(false);
  const [editingTeeTime, setEditingTeeTime] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [defaultPlayersPerTeeTime, setDefaultPlayersPerTeeTime] = useState(4);
  const [initialStartTime, setInitialStartTime] = useState('09:00');
  const [timeBetweenTeeTimes, setTimeBetweenTeeTimes] = useState(9);
  const [teeTimeForm, setTeeTimeForm] = useState({
    name: '',
    teeTime: '',
    startingHole: 1,
    maxPlayers: 4
  });

  useEffect(() => {
    fetchData();
  }, [tournamentId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch tournament
      const tournamentDoc = await getDoc(doc(db, 'tournaments', tournamentId));
      if (!tournamentDoc.exists()) {
        throw new Error('Tournament not found');
      }

      const tournamentData = { id: tournamentDoc.id, ...tournamentDoc.data() };
      setTournament(tournamentData);

      // Fetch players
      const playersQuery = query(
        collection(db, 'players'),
        where('organizationId', '==', tournamentData.organizationId)
      );
      const playersSnapshot = await getDocs(playersQuery);
      const playersData = playersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPlayers(playersData);

      // Load tee times from tournament data (still using 'flights' field for backward compatibility)
      const teeTimesData = tournamentData.flights || [];
      setTeeTimes(teeTimesData);

      // Calculate unassigned players
      const assignedPlayerIds = new Set();
      teeTimesData.forEach(teeTime => {
        teeTime.playerIds?.forEach(id => assignedPlayerIds.add(id));
      });
      
      const unassigned = playersData.filter(player => !assignedPlayerIds.has(player.id));
      setUnassignedPlayers(unassigned);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateNextTeeTime = () => {
    if (teeTimes.length === 0) {
      return initialStartTime;
    }

    // Find the last tee time with a tee time
    const lastTeeTime = [...teeTimes].reverse().find(t => t.teeTime);
    if (!lastTeeTime || !lastTeeTime.teeTime) {
      return initialStartTime;
    }

    // Parse the last tee time and add the interval
    const [hours, minutes] = lastTeeTime.teeTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + timeBetweenTeeTimes;
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;

    return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
  };

  const handleCreateTeeTime = () => {
    setEditingTeeTime(null);
    setTeeTimeForm({
      name: `Tee Time ${teeTimes.length + 1}`,
      teeTime: calculateNextTeeTime(),
      startingHole: 1,
      maxPlayers: defaultPlayersPerTeeTime
    });
    setOpenTeeTimeDialog(true);
  };

  const handleEditTeeTime = (teeTime) => {
    setEditingTeeTime(teeTime);
    setTeeTimeForm({
      name: teeTime.name,
      teeTime: teeTime.teeTime || '',
      startingHole: teeTime.startingHole || 1,
      maxPlayers: teeTime.maxPlayers || 4
    });
    setOpenTeeTimeDialog(true);
  };

  const handleSaveTeeTime = async () => {
    try {
      let updatedTeeTimes;

      if (editingTeeTime) {
        // Update existing tee time
        updatedTeeTimes = teeTimes.map(t => 
          t.id === editingTeeTime.id 
            ? { ...t, ...teeTimeForm }
            : t
        );
      } else {
        // Create new tee time
        const newTeeTime = {
          id: Date.now().toString(),
          ...teeTimeForm,
          playerIds: []
        };
        updatedTeeTimes = [...teeTimes, newTeeTime];
      }

      await updateDoc(doc(db, 'tournaments', tournamentId), {
        flights: updatedTeeTimes // Still using 'flights' field for backward compatibility
      });

      setTeeTimes(updatedTeeTimes);
      setOpenTeeTimeDialog(false);
      setEditingTeeTime(null);

    } catch (error) {
      console.error('Error saving tee time:', error);
      alert('Error saving tee time: ' + error.message);
    }
  };

  const handleDeleteTeeTime = async (teeTimeId) => {
    if (!window.confirm('Are you sure you want to delete this tee time?')) return;

    try {
      const updatedTeeTimes = teeTimes.filter(t => t.id !== teeTimeId);
      
      await updateDoc(doc(db, 'tournaments', tournamentId), {
        flights: updatedTeeTimes // Still using 'flights' field for backward compatibility
      });

      setTeeTimes(updatedTeeTimes);
      fetchData(); // Refresh to update unassigned players

    } catch (error) {
      console.error('Error deleting tee time:', error);
      alert('Error deleting tee time: ' + error.message);
    }
  };

  const handleAddPlayerToTeeTime = async (teeTimeId, playerId) => {
    try {
      const updatedTeeTimes = teeTimes.map(t => {
        if (t.id === teeTimeId) {
          if (t.playerIds?.length >= t.maxPlayers) {
            alert('Tee time is full!');
            return t;
          }
          return {
            ...t,
            playerIds: [...(t.playerIds || []), playerId]
          };
        }
        return t;
      });

      await updateDoc(doc(db, 'tournaments', tournamentId), {
        flights: updatedTeeTimes // Still using 'flights' field for backward compatibility
      });

      setTeeTimes(updatedTeeTimes);
      fetchData(); // Refresh to update unassigned players

    } catch (error) {
      console.error('Error adding player to tee time:', error);
      alert('Error adding player: ' + error.message);
    }
  };

  const handleRemovePlayerFromTeeTime = async (teeTimeId, playerId) => {
    try {
      const updatedTeeTimes = teeTimes.map(t => {
        if (t.id === teeTimeId) {
          return {
            ...t,
            playerIds: t.playerIds.filter(id => id !== playerId)
          };
        }
        return t;
      });

      await updateDoc(doc(db, 'tournaments', tournamentId), {
        flights: updatedTeeTimes // Still using 'flights' field for backward compatibility
      });

      setTeeTimes(updatedTeeTimes);
      fetchData(); // Refresh to update unassigned players

    } catch (error) {
      console.error('Error removing player from tee time:', error);
      alert('Error removing player: ' + error.message);
    }
  };

  const handleAutoAssign = async () => {
    if (!window.confirm('This will automatically assign all unassigned players to tee times. Continue?')) return;

    try {
      const playersPerTeeTime = defaultPlayersPerTeeTime;
      const newTeeTimes = [...teeTimes];
      const playersToAssign = [...unassignedPlayers];

      // Calculate starting tee time
      let currentTeeTime = initialStartTime;
      if (newTeeTimes.length > 0) {
        // Find the last tee time's tee time
        const lastTeeTime = [...newTeeTimes].reverse().find(t => t.teeTime);
        if (lastTeeTime && lastTeeTime.teeTime) {
          const [hours, minutes] = lastTeeTime.teeTime.split(':').map(Number);
          const totalMinutes = hours * 60 + minutes + timeBetweenTeeTimes;
          const newHours = Math.floor(totalMinutes / 60) % 24;
          const newMinutes = totalMinutes % 60;
          currentTeeTime = `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
        }
      }

      // Create new tee times as needed
      while (playersToAssign.length > 0) {
        // Find a tee time with space
        let targetTeeTime = newTeeTimes.find(t => (t.playerIds?.length || 0) < t.maxPlayers);

        if (!targetTeeTime) {
          // Create new tee time with auto-incremented tee time
          targetTeeTime = {
            id: Date.now().toString() + Math.random(),
            name: `Tee Time ${newTeeTimes.length + 1}`,
            teeTime: currentTeeTime,
            startingHole: 1,
            maxPlayers: playersPerTeeTime,
            playerIds: []
          };
          newTeeTimes.push(targetTeeTime);

          // Calculate next tee time
          const [hours, minutes] = currentTeeTime.split(':').map(Number);
          const totalMinutes = hours * 60 + minutes + timeBetweenTeeTimes;
          const newHours = Math.floor(totalMinutes / 60) % 24;
          const newMinutes = totalMinutes % 60;
          currentTeeTime = `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
        }

        // Add players to tee time
        const spacesAvailable = targetTeeTime.maxPlayers - (targetTeeTime.playerIds?.length || 0);
        const playersToAdd = playersToAssign.splice(0, spacesAvailable);
        targetTeeTime.playerIds = [...(targetTeeTime.playerIds || []), ...playersToAdd.map(p => p.id)];
      }

      await updateDoc(doc(db, 'tournaments', tournamentId), {
        flights: newTeeTimes // Still using 'flights' field for backward compatibility
      });

      setTeeTimes(newTeeTimes);
      fetchData();
      alert('Players assigned successfully!');

    } catch (error) {
      console.error('Error auto-assigning players:', error);
      alert('Error auto-assigning players: ' + error.message);
    }
  };

  const handleDeleteAllTeeTimes = async () => {
    if (!window.confirm('Are you sure you want to delete ALL tee times? This will unassign all players.')) return;

    try {
      await updateDoc(doc(db, 'tournaments', tournamentId), {
        flights: [] // Still using 'flights' field for backward compatibility
      });

      setTeeTimes([]);
      fetchData();
      alert('All tee times deleted successfully!');

    } catch (error) {
      console.error('Error deleting all tee times:', error);
      alert('Error deleting all tee times: ' + error.message);
    }
  };

  const getPlayerName = (playerId) => {
    const player = players.find(p => p.id === playerId);
    return player ? `${player.firstName} ${player.lastName}` : 'Unknown';
  };

  const getPlayerHandicap = (playerId) => {
    const player = players.find(p => p.id === playerId);
    return player?.handicap || 0;
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!tournament) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">Tournament not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/tournaments')}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4">{tournament.name}</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Tee Time & Group Management
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            type="time"
            label="Initial Start Time"
            value={initialStartTime}
            onChange={(e) => setInitialStartTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Time Between</InputLabel>
            <Select
              value={timeBetweenTeeTimes}
              label="Time Between"
              onChange={(e) => setTimeBetweenTeeTimes(e.target.value)}
            >
              <MenuItem value={6}>6 minutes</MenuItem>
              <MenuItem value={7}>7 minutes</MenuItem>
              <MenuItem value={8}>8 minutes</MenuItem>
              <MenuItem value={9}>9 minutes</MenuItem>
              <MenuItem value={10}>10 minutes</MenuItem>
              <MenuItem value={11}>11 minutes</MenuItem>
              <MenuItem value={12}>12 minutes</MenuItem>
              <MenuItem value={13}>13 minutes</MenuItem>
              <MenuItem value={14}>14 minutes</MenuItem>
              <MenuItem value={15}>15 minutes</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Players Per Tee Time</InputLabel>
            <Select
              value={defaultPlayersPerTeeTime}
              label="Players Per Tee Time"
              onChange={(e) => setDefaultPlayersPerTeeTime(e.target.value)}
            >
              <MenuItem value={2}>2 Players</MenuItem>
              <MenuItem value={3}>3 Players</MenuItem>
              <MenuItem value={4}>4 Players</MenuItem>
              <MenuItem value={5}>5 Players</MenuItem>
              <MenuItem value={6}>6 Players</MenuItem>
              <MenuItem value={7}>7 Players</MenuItem>
              <MenuItem value={8}>8 Players</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Shuffle />}
            onClick={handleAutoAssign}
            disabled={unassignedPlayers.length === 0}
          >
            Auto-Assign
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateTeeTime}
          >
            Create Tee Time
          </Button>
          {teeTimes.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={handleDeleteAllTeeTimes}
            >
              Delete All
            </Button>
          )}
        </Box>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="body2" gutterBottom>
            <strong>Tee Time Settings:</strong> Starting at {initialStartTime}, {defaultPlayersPerTeeTime} players per tee time, {timeBetweenTeeTimes} minutes apart
          </Typography>
          <Typography variant="caption">
            • Tee times will auto-increment by {timeBetweenTeeTimes} minutes for each new tee time
            <br />
            • Next tee time will be scheduled at: <strong>{calculateNextTeeTime()}</strong>
            <br />
            • You can manually adjust tee times when editing individual tee times
          </Typography>
        </Box>
      </Alert>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Total Tee Times</Typography>
              <Typography variant="h4">{teeTimes.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Total Players</Typography>
              <Typography variant="h4">{players.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Assigned</Typography>
              <Typography variant="h4">{players.length - unassignedPlayers.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Unassigned</Typography>
              <Typography variant="h4" color={unassignedPlayers.length > 0 ? 'error' : 'success'}>
                {unassignedPlayers.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)}>
          <Tab label="Tee Times" icon={<Group />} iconPosition="start" />
          <Tab label="Unassigned Players" icon={<Person />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tee Times Tab */}
      {tabValue === 0 && (
        <Grid container spacing={2}>
          {teeTimes.length === 0 ? (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Group sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>No Tee Times Created</Typography>
                <Typography color="text.secondary" mb={2}>
                  Create tee times to organize players into groups
                </Typography>
                <Button variant="contained" startIcon={<Add />} onClick={handleCreateTeeTime}>
                  Create First Tee Time
                </Button>
              </Paper>
            </Grid>
          ) : (
            teeTimes.map((teeTime) => (
              <Grid item xs={12} md={6} key={teeTime.id}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Box>
                        <Typography variant="h6">{teeTime.name}</Typography>
                        <Box display="flex" gap={2} mt={1}>
                          {teeTime.teeTime && (
                            <Chip
                              icon={<Timer />}
                              label={teeTime.teeTime}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          <Chip
                            icon={<Schedule />}
                            label={`Hole ${teeTime.startingHole}`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={`${teeTime.playerIds?.length || 0}/${teeTime.maxPlayers}`}
                            size="small"
                            color={(teeTime.playerIds?.length || 0) === teeTime.maxPlayers ? 'success' : 'default'}
                          />
                        </Box>
                      </Box>
                      <Box>
                        <IconButton onClick={() => handleEditTeeTime(teeTime)} size="small">
                          <Edit />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleDeleteTeeTime(teeTime.id)} 
                          size="small"
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" gutterBottom>Players:</Typography>
                    <List dense>
                      {teeTime.playerIds?.length === 0 ? (
                        <ListItem>
                          <ListItemText 
                            primary="No players assigned"
                            secondary="Drag players here or use auto-assign"
                          />
                        </ListItem>
                      ) : (
                        teeTime.playerIds?.map((playerId, index) => (
                          <ListItem key={playerId}>
                            <ListItemText
                              primary={`${index + 1}. ${getPlayerName(playerId)}`}
                              secondary={`Handicap: ${getPlayerHandicap(playerId)}`}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => handleRemovePlayerFromTeeTime(teeTime.id, playerId)}
                              >
                                <Delete />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))
                      )}
                    </List>

                    {(teeTime.playerIds?.length || 0) < teeTime.maxPlayers && unassignedPlayers.length > 0 && (
                      <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                        <InputLabel>Add Player</InputLabel>
                        <Select
                          label="Add Player"
                          value=""
                          onChange={(e) => handleAddPlayerToTeeTime(teeTime.id, e.target.value)}
                        >
                          {unassignedPlayers.map(player => (
                            <MenuItem key={player.id} value={player.id}>
                              {player.firstName} {player.lastName} (HCP: {player.handicap || 0})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* Unassigned Players Tab */}
      {tabValue === 1 && (
        <Paper>
          <List>
            {unassignedPlayers.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary="All players assigned!"
                  secondary="All players have been assigned to tee times"
                />
              </ListItem>
            ) : (
              unassignedPlayers.map(player => (
                <ListItem key={player.id}>
                  <ListItemText
                    primary={`${player.firstName} ${player.lastName}`}
                    secondary={`Handicap: ${player.handicap || 0}`}
                  />
                </ListItem>
              ))
            )}
          </List>
        </Paper>
      )}

      {/* Create/Edit Tee Time Dialog */}
      <Dialog open={openTeeTimeDialog} onClose={() => setOpenTeeTimeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTeeTime ? 'Edit Tee Time' : 'Create New Tee Time'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Tee Time Name"
            fullWidth
            variant="outlined"
            value={teeTimeForm.name}
            onChange={(e) => setTeeTimeForm({ ...teeTimeForm, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Tee Time"
            type="time"
            fullWidth
            variant="outlined"
            value={teeTimeForm.teeTime}
            onChange={(e) => setTeeTimeForm({ ...teeTimeForm, teeTime: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense"
            label="Starting Hole"
            type="number"
            fullWidth
            variant="outlined"
            value={teeTimeForm.startingHole}
            onChange={(e) => setTeeTimeForm({ ...teeTimeForm, startingHole: parseInt(e.target.value) })}
            inputProps={{ min: 1, max: 18 }}
          />
          <TextField
            margin="dense"
            label="Max Players"
            type="number"
            fullWidth
            variant="outlined"
            value={teeTimeForm.maxPlayers}
            onChange={(e) => setTeeTimeForm({ ...teeTimeForm, maxPlayers: parseInt(e.target.value) })}
            inputProps={{ min: 2, max: 8 }}
            helperText="2-8 players per tee time"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTeeTimeDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveTeeTime} variant="contained">
            {editingTeeTime ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

